import { Injectable } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { LocalStorageService } from '../local-storage/local-storage.service';
import * as dayjs from 'dayjs';
import 'dayjs/locale/fr';
import 'dayjs/locale/en';
import 'dayjs/locale/es';
import 'dayjs/locale/it';

@Injectable({
  providedIn: 'root',
})
export class LanguageService {
  private readonly SUPPORTED_LANGUAGES = ['fr', 'en', 'es', 'it'];
  private readonly DEFAULT_LANGUAGE = 'en';
  private readonly STORAGE_KEY = 'userLanguagePreference';

  constructor(
    private translateService: TranslateService,
    private localStorageService: LocalStorageService,
  ) {}

  /**
   * Initialize language at app startup
   * Priority: 1) localStorage preference (if valid), 2) browser language (if supported), 3) fallback to English
   */
  initLanguage(): Promise<void> {
    const language = this.determineLanguage();

    // Configure translate service
    this.translateService.setDefaultLang(this.DEFAULT_LANGUAGE);

    // Configure dayjs locale
    this.setDayjsLocale(language);

    return this.translateService
      .use(language)
      .toPromise()
      .then(() => {
        console.log(`Language initialized: ${language}`);
      }) as Promise<void>;
  }

  /**
   * Set dayjs locale based on language
   */
  private setDayjsLocale(language: string): void {
    dayjs.locale(language);
  }

  /**
   * Determine which language to use based on priority
   */
  private determineLanguage(): string {
    // 1. Check localStorage preference
    const storedLanguage = this.localStorageService.get<string>(this.STORAGE_KEY);
    if (storedLanguage && this.SUPPORTED_LANGUAGES.includes(storedLanguage)) {
      return storedLanguage;
    }

    // 2. Check browser language
    const browserLang = this.getBrowserLanguage();
    if (this.SUPPORTED_LANGUAGES.includes(browserLang)) {
      return browserLang;
    }

    // 3. Fallback to default
    return this.DEFAULT_LANGUAGE;
  }

  /**
   * Get browser language (first 2 characters, lowercase)
   */
  private getBrowserLanguage(): string {
    return window.navigator.language.slice(0, 2).toLowerCase();
  }

  /**
   * Get current active language
   */
  getCurrentLanguage(): string {
    return this.translateService.currentLang || this.DEFAULT_LANGUAGE;
  }

  /**
   * Get detected browser language (for display in UI)
   */
  getDetectedLanguage(): string {
    const browserLang = this.getBrowserLanguage();
    return this.SUPPORTED_LANGUAGES.includes(browserLang) ? browserLang : this.DEFAULT_LANGUAGE;
  }

  /**
   * Switch language
   * @param language Language code ('fr', 'en', 'es', 'it') or 'auto'
   */
  switchLanguage(language: string): void {
    if (language === 'auto') {
      // Remove stored preference to use auto-detection
      this.localStorageService.remove(this.STORAGE_KEY);
    } else if (this.SUPPORTED_LANGUAGES.includes(language)) {
      // Save preference
      this.localStorageService.save(this.STORAGE_KEY, language);
    } else {
      console.error(`Unsupported language: ${language}`);
      return;
    }

    // Reload page to apply new language (and re-initialize Angular LOCALE_ID)
    window.location.reload();
  }

  /**
   * Check if user has a stored language preference
   */
  hasStoredPreference(): boolean {
    const stored = this.localStorageService.get<string>(this.STORAGE_KEY);
    return stored !== undefined && stored !== null && this.SUPPORTED_LANGUAGES.includes(stored);
  }

  /**
   * Get supported languages for dropdown
   */
  getSupportedLanguages(): Array<{ label: string; value: string }> {
    return [
      { label: 'Automatic', value: 'auto' },
      { label: 'English', value: 'en' },
      { label: 'Français', value: 'fr' },
      { label: 'Español', value: 'es' },
      { label: 'Italiano', value: 'it' },
    ];
  }
}
